import { useState, useEffect, useRef, FormEvent } from 'react'
import { Button, Checkbox, Input } from '@pay-com/components'
import { Logo } from '@pay-com/icons'
import { useAuthToken } from 'hooks/use-auth-token'
import {
  useAuthenticatePaypassWithVerificationCodeMutation,
  useSendAuthorizationCodeMutation
} from 'generated/graphql'

const CODE_MIN_LENGTH = 6
const ONE_MINUTE = 60 * 1000

export const CodeAuthorization = () => {
  const [code, setCode] = useState('')
  const [staySignedIn, setStaySignedIn] = useState(false)
  const [isWrongCode, setIsWrongCode] = useState(false)
  const [isCodeAlreadyUsed, setIsCodeAlreadyUsed] = useState(false)

  const isCodeSent = useRef(false)
  const decodedToken = useAuthToken()

  const [sendCode] = useSendAuthorizationCodeMutation()

  const [authenticate, { loading: authenticating }] =
    useAuthenticatePaypassWithVerificationCodeMutation({
      refetchQueries: ['doesPaypassNeedCodeAuthentication'],
      onCompleted: data => {
        const result = data.authenticatePaypassWithVerificationCode

        if (result?.success) {
          setIsWrongCode(false)
          return
        }

        if (result?.message === 'workflow_already_done_new_code_needed') {
          resendCode(true)
          setIsCodeAlreadyUsed(true)
          return
        }

        setIsWrongCode(true)
      },
      onError: () => {
        setIsWrongCode(true)
      }
    })

  const resendCode = (bypassIsCodeSentCheck = false) => {
    if (isCodeSent.current && !bypassIsCodeSentCheck) {
      return
    }

    isCodeSent.current = true
    sendCode()
  }

  useEffect(() => {
    const lastCodeSentTime = Number(sessionStorage.getItem('lastCodeSentTime'))
    const now = Date.now()

    if (!lastCodeSentTime || now - lastCodeSentTime > ONE_MINUTE) {
      resendCode()
      sessionStorage.setItem('lastCodeSentTime', now.toString())
    }
  }, [])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsCodeAlreadyUsed(false)
    setIsWrongCode(false)

    authenticate({
      variables: {
        input: {
          code,
          shouldStayLoggedIn: staySignedIn
        }
      }
    })
  }

  const partlyHiddenEmail =
    decodedToken?.email.replace(
      /(.{2})(.*)(@.*)/,
      (_, p1, p2, p3) => `${p1}${'*'.repeat(p2.length)}${p3}`
    ) ?? ''

  return (
    <div className="fixed left-0 top-0 z-10 flex size-full items-center justify-center bg-white p-6">
      <div className="flex flex-col items-center justify-center gap-12 md:gap-24">
        <Logo />

        <form
          className="flex flex-col items-center justify-center gap-6"
          onSubmit={handleSubmit}
        >
          <h1 className="text-lg font-semibold leading-none md:text-2xl md:tracking-wide">
            Verify your Identity
          </h1>
          <h2 className="max-w-3xl text-center text-base font-thin leading-none md:text-xl md:tracking-wide">
            Please enter the verification code you received via email (
            {partlyHiddenEmail}) below to continue with activating this account.
          </h2>

          <div className="flex w-full flex-col gap-2 md:w-72 md:pt-4">
            <Input
              label="Verification Code"
              placeholder="Enter your code"
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              required
              disabled={authenticating}
            />
            <Checkbox
              label="Stay signed in for a week"
              checked={staySignedIn}
              onChange={() => setStaySignedIn(prev => !prev)}
              disabled={authenticating}
            />
            {isCodeAlreadyUsed && (
              <p className="text-sm font-thin text-red-500">
                The code you entered has already been used. Please request a new
                code. We have sent you a new code to your email.
              </p>
            )}
            {isWrongCode && (
              <p className="text-sm font-thin text-red-500">
                The code you entered is incorrect. Please try again.
              </p>
            )}
          </div>

          <Button
            className="w-full md:w-72"
            disabled={code.length < CODE_MIN_LENGTH || authenticating}
            isLoading={authenticating}
            type="submit"
            size="medium"
          >
            Continue with activation
          </Button>
        </form>
      </div>
    </div>
  )
}
